import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabase';
import { AlertTriangle, CheckCircle, Calendar, User, Monitor, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, ToastType } from '../../components/Toast';

interface UserError {
  id: string;
  user_id: string | null;
  session_id: string | null;
  screen_name: string;
  error_type: string;
  error_message: string;
  stack_trace: string | null;
  component_stack: string | null;
  user_agent: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  user_email?: string;
  user_company?: string;
  user_first_name?: string;
  internal_user_id?: string;
  generation_type?: string;
  error_category?: string;
  retry_attempt?: number;
  blog_id?: string;
  campaign_id?: string;
  technical_details?: Record<string, any>;
}

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

type FilterType = 'all' | 'unresolved' | 'resolved' | 'generation';

export function AdminErrors() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<UserError[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('unresolved');
  const [selectedError, setSelectedError] = useState<UserError | null>(null);
  const [sessionFilter, setSessionFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<{ id: string; email: string } | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((type: ToastType, title: string, message?: string) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, type, title, message }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    loadErrors();
  }, [filter, sessionFilter, userFilter]);

  const loadErrors = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('user_errors')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filter === 'unresolved') {
        query = query.is('resolved_at', null);
      } else if (filter === 'resolved') {
        query = query.not('resolved_at', 'is', null);
      } else if (filter === 'generation') {
        query = query.not('generation_type', 'is', null).is('resolved_at', null);
      }

      if (sessionFilter) {
        query = query.eq('session_id', sessionFilter);
      }

      if (userFilter) {
        query = query.eq('user_id', userFilter.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const userIds = [...new Set((data || []).map(e => e.user_id).filter(Boolean))];

      let userMap: Record<string, { id: string; email: string; company_name: string | null; first_name: string | null }> = {};

      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, auth_user_id, email, company_name, first_name')
          .in('id', userIds);

        for (const u of usersData || []) {
          userMap[u.id] = u;
        }
      }

      const errorsWithUsers = (data || []).map((err) => {
        if (err.user_id && userMap[err.user_id]) {
          const u = userMap[err.user_id];
          return {
            ...err,
            user_email: u.email,
            user_company: u.company_name,
            user_first_name: u.first_name,
            internal_user_id: u.id,
          };
        }
        return err;
      });

      setErrors(errorsWithUsers);
    } catch (error) {
      console.error('Error loading errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAdminUserId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    return adminUser?.id || null;
  };

  const handleResolve = async (errorId: string) => {
    try {
      const adminUserId = await getAdminUserId();

      const { error } = await supabase
        .from('user_errors')
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: adminUserId,
        })
        .eq('id', errorId);

      if (error) throw error;

      await loadErrors();
      setSelectedError(null);
      addToast('success', 'Error opgelost', 'De error is gemarkeerd als opgelost.');
    } catch (error) {
      console.error('Error resolving error:', error);
      addToast('error', 'Fout opgetreden', 'De error kon niet worden gemarkeerd als opgelost.');
    }
  };

  const handleBulkResolveSession = async () => {
    if (!sessionFilter) return;

    const unresolvedCount = errors.filter(e => !e.resolved_at).length;

    if (unresolvedCount === 0) {
      addToast('info', 'Niets te doen', 'Alle errors voor deze sessie zijn al opgelost.');
      return;
    }

    try {
      const adminUserId = await getAdminUserId();

      const { error } = await supabase
        .from('user_errors')
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: adminUserId,
        })
        .eq('session_id', sessionFilter)
        .is('resolved_at', null);

      if (error) throw error;

      await loadErrors();
      addToast('success', `${unresolvedCount} error(s) opgelost`, 'Alle openstaande errors voor deze sessie zijn gemarkeerd als opgelost.');
    } catch (error) {
      console.error('Error bulk resolving errors:', error);
      addToast('error', 'Fout opgetreden', 'De errors konden niet worden gemarkeerd als opgelost.');
    }
  };

  const handleBulkResolveUser = async () => {
    if (!userFilter) return;

    const unresolvedCount = errors.filter(e => !e.resolved_at).length;

    if (unresolvedCount === 0) {
      addToast('info', 'Niets te doen', 'Alle errors voor deze gebruiker zijn al opgelost.');
      return;
    }

    try {
      const adminUserId = await getAdminUserId();

      const { error } = await supabase
        .from('user_errors')
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: adminUserId,
        })
        .eq('user_id', userFilter.id)
        .is('resolved_at', null);

      if (error) throw error;

      await loadErrors();
      addToast('success', `${unresolvedCount} error(s) opgelost`, `Alle openstaande errors van ${userFilter.email} zijn gemarkeerd als opgelost.`);
    } catch (error) {
      console.error('Error bulk resolving user errors:', error);
      addToast('error', 'Fout opgetreden', 'De errors konden niet worden gemarkeerd als opgelost.');
    }
  };

  const handleSetUserFilter = (userId: string, displayName: string) => {
    setSessionFilter('');
    setUserFilter({ id: userId, email: displayName });
  };

  const getErrorTypeColor = (type: string) => {
    switch (type) {
      case 'generation_error':
        return 'bg-red-100 text-red-800';
      case 'react_error':
        return 'bg-red-100 text-red-800';
      case 'api_error':
        return 'bg-orange-100 text-orange-800';
      case 'network_error':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGenerationTypeLabel = (type: string) => {
    switch (type) {
      case 'blog_idea':
        return 'Blog Idee';
      case 'blog_title':
        return 'Blog Titel';
      case 'blog_intro':
        return 'Blog Intro';
      case 'blog_content':
        return 'Blog Artikel';
      case 'blog_image':
        return 'Blog Afbeelding';
      case 'campaign_email':
        return 'Campaign Email';
      case 'campaign_variant':
        return 'Campaign Variant';
      default:
        return type;
    }
  };

  const getErrorCategoryColor = (category: string) => {
    switch (category) {
      case 'OPENAI_UNAVAILABLE':
        return 'bg-red-100 text-red-800';
      case 'TIMEOUT':
        return 'bg-orange-100 text-orange-800';
      case 'NETWORK_ERROR':
        return 'bg-yellow-100 text-yellow-800';
      case 'JAVASCRIPT_ERROR':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Zojuist';
    if (diffMins < 60) return `${diffMins} min geleden`;
    if (diffHours < 24) return `${diffHours} uur geleden`;
    if (diffDays < 7) return `${diffDays} dagen geleden`;

    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const unresolvedCount = errors.filter(e => !e.resolved_at).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Errors</h1>
            <p className="text-gray-600 mt-1">
              Frontend errors en crashes van gebruikers
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilter('generation')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'generation'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Generatie Errors
            </button>
            <button
              onClick={() => setFilter('unresolved')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'unresolved'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Alle Open
            </button>
            <button
              onClick={() => setFilter('resolved')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'resolved'
                  ? 'bg-[#231F20] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Opgelost
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Filter op Sessie ID:
            </label>
            <input
              type="text"
              value={sessionFilter}
              onChange={(e) => {
                setUserFilter(null);
                setSessionFilter(e.target.value);
              }}
              placeholder="Voer sessie ID in..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#231F20] focus:border-transparent"
            />
            {(sessionFilter || userFilter) && (
              <button
                onClick={() => {
                  setSessionFilter('');
                  setUserFilter(null);
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
              >
                Wis filter
              </button>
            )}
          </div>

          {userFilter && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
              <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-blue-800">
                Gefilterd op gebruiker: <span className="font-medium">{userFilter.email}</span>
              </span>
            </div>
          )}

          {sessionFilter && !loading && unresolvedCount > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={handleBulkResolveSession}
                className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Markeer alle {unresolvedCount} error(s) als opgelost
              </button>
            </div>
          )}

          {userFilter && !loading && unresolvedCount > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={handleBulkResolveUser}
                className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Markeer alle {unresolvedCount} error(s) van {userFilter.email} als opgelost
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#231F20] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : errors.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Geen errors gevonden
            </h3>
            <p className="text-gray-600">
              {filter === 'unresolved'
                ? 'Er zijn geen openstaande errors.'
                : filter === 'resolved'
                ? 'Er zijn geen opgeloste errors.'
                : filter === 'generation'
                ? 'Er zijn geen openstaande generatie errors.'
                : 'Er zijn nog geen errors gelogd.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                    Error
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessie ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gebruiker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tijdstip
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {errors.map((error) => (
                  <tr key={error.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="max-w-xs">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {error.error_message}
                        </p>
                        {error.generation_type && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {getGenerationTypeLabel(error.generation_type)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {error.error_category ? (
                        <span
                          className={`inline-block px-2 py-1 text-xs font-medium rounded ${getErrorCategoryColor(
                            error.error_category
                          )}`}
                        >
                          {error.error_category.replace('_', ' ')}
                        </span>
                      ) : (
                        <span
                          className={`inline-block px-2 py-1 text-xs font-medium rounded ${getErrorTypeColor(
                            error.error_type
                          )}`}
                        >
                          {error.error_type.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {error.session_id ? (
                        <button
                          onClick={() => {
                            setUserFilter(null);
                            setSessionFilter(error.session_id!);
                          }}
                          className="text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[100px] block"
                          title={error.session_id}
                        >
                          {error.session_id.substring(0, 8)}...
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {error.user_id ? (
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => navigate(`/admin/users/${error.internal_user_id || error.user_id}`)}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[160px] text-left font-medium"
                            title="Ga naar gebruikersprofiel"
                          >
                            {error.user_company || error.user_first_name || error.user_email || 'Onbekend'}
                          </button>
                          {error.user_email && (error.user_company || error.user_first_name) && (
                            <span className="text-xs text-gray-400 truncate max-w-[160px]">
                              {error.user_email}
                            </span>
                          )}
                          <button
                            onClick={() => handleSetUserFilter(error.user_id!, error.user_company || error.user_email || 'Gebruiker')}
                            className="text-xs text-gray-400 hover:text-gray-600 hover:underline text-left"
                            title="Filter op deze gebruiker"
                          >
                            Filter gebruiker
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Anoniem</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(error.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedError(error)}
                          className="text-[#231F20] hover:text-[#231F20]/80 font-medium text-sm"
                        >
                          Details
                        </button>
                        {!error.resolved_at && (
                          <button
                            onClick={() => handleResolve(error.id)}
                            className="text-green-600 hover:text-green-800 font-medium text-sm"
                            title="Markeer als opgelost"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Error Details</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDate(selectedError.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedError(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Error Message</h3>
                  <p className="text-sm text-gray-700 bg-red-50 p-4 rounded border border-red-200">
                    {selectedError.error_message}
                  </p>
                </div>

                {selectedError.generation_type && (
                  <div className="grid grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                    <div>
                      <h3 className="text-xs font-medium text-gray-700 mb-1">Generatie Type</h3>
                      <p className="text-sm font-medium text-gray-900">
                        {getGenerationTypeLabel(selectedError.generation_type)}
                      </p>
                    </div>
                    {selectedError.error_category && (
                      <div>
                        <h3 className="text-xs font-medium text-gray-700 mb-1">Error Categorie</h3>
                        <span
                          className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getErrorCategoryColor(
                            selectedError.error_category
                          )}`}
                        >
                          {selectedError.error_category}
                        </span>
                      </div>
                    )}
                    {selectedError.retry_attempt !== undefined && selectedError.retry_attempt > 0 && (
                      <div>
                        <h3 className="text-xs font-medium text-gray-700 mb-1">Retry Pogingen</h3>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedError.retry_attempt}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Error Type
                    </h3>
                    <span
                      className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getErrorTypeColor(
                        selectedError.error_type
                      )}`}
                    >
                      {selectedError.error_type}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      Screen
                    </h3>
                    <p className="text-sm text-gray-700">{selectedError.screen_name}</p>
                  </div>

                  {selectedError.session_id && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-2">Sessie ID</h3>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                          {selectedError.session_id}
                        </code>
                        <button
                          onClick={() => {
                            setSelectedError(null);
                            setUserFilter(null);
                            setSessionFilter(selectedError.session_id!);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Filter sessie
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Gebruiker
                    </h3>
                    {selectedError.user_id ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              setSelectedError(null);
                              navigate(`/admin/users/${selectedError.internal_user_id || selectedError.user_id}`);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {selectedError.user_company || selectedError.user_first_name || selectedError.user_email || selectedError.user_id}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedError(null);
                              handleSetUserFilter(selectedError.user_id!, selectedError.user_company || selectedError.user_email || 'Gebruiker');
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Filter gebruiker
                          </button>
                        </div>
                        {selectedError.user_email && (selectedError.user_company || selectedError.user_first_name) && (
                          <span className="text-xs text-gray-500">{selectedError.user_email}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Anoniem</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Status
                    </h3>
                    {selectedError.resolved_at ? (
                      <span className="inline-flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Opgelost op {formatDate(selectedError.resolved_at)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-sm text-orange-600">
                        <AlertTriangle className="w-4 h-4" />
                        Open
                      </span>
                    )}
                  </div>
                </div>

                {selectedError.stack_trace && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Stack Trace</h3>
                    <pre className="text-xs text-gray-700 bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto">
                      {selectedError.stack_trace}
                    </pre>
                  </div>
                )}

                {selectedError.component_stack && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Component Stack</h3>
                    <pre className="text-xs text-gray-700 bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto">
                      {selectedError.component_stack}
                    </pre>
                  </div>
                )}

                {selectedError.technical_details && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Technische Details</h3>
                    <pre className="text-xs text-gray-700 bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto">
                      {JSON.stringify(selectedError.technical_details, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedError.user_agent && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">User Agent</h3>
                    <p className="text-xs text-gray-700 bg-gray-50 p-4 rounded border border-gray-200">
                      {selectedError.user_agent}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setSelectedError(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Sluiten
                </button>
                {!selectedError.resolved_at && (
                  <button
                    onClick={() => handleResolve(selectedError.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Markeer als opgelost
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </AdminLayout>
  );
}
